import type { MedplumClient } from "@medplum/core";
import type {
  Observation,
  Practitioner,
  QuestionnaireResponse,
  Reference,
} from "@medplum/fhirtypes";

import * as Sentry from "@sentry/react";

import { getEditedByReference } from "@/encounters/components/EncounterLabor/services/labor";
import type { ResolveLaborAmendProvenancePractitionerParams } from "@/encounters/components/EncounterLabor/types/labor-amend-completion-provenance";
import type { LaborAmendProvenanceSnapshot } from "@/encounters/types/labor-amend-provenance";
import {
  getObservationEncounterAmendParentVersionId,
  getQuestionnaireResponseEncounterParentVersionId,
} from "@/encounters/utils/questionnaire-response-amend-stamp";
import { Extensions } from "@/fhir/enums/extensions";
import { getExtensionValues, isPractitionerReference, joinReferenceString } from "@/utils/medplum";
import { fetchAllPaginatedResources } from "@/utils/pagination";

function isPractitionerReferenceString(reference: string | undefined): reference is string {
  return isPractitionerReference(reference ? { reference } : undefined);
}

/**
 * Reads a Practitioner reference from flowsheet observation authorship fields.
 */
function resolvePractitionerReferenceFromObservation(
  observation: Observation,
): Reference<Practitioner> | undefined {
  const editedByReferenceValue = getEditedByReference(observation);
  if (isPractitionerReferenceString(editedByReferenceValue)) {
    return { reference: editedByReferenceValue };
  }

  const performerReference = observation.performer?.find((performer) =>
    isPractitionerReferenceString(performer.reference),
  );
  if (performerReference?.reference) {
    return { reference: performerReference.reference };
  }

  return undefined;
}

/**
 * Reads the EditedBy Practitioner reference from a QuestionnaireResponse extension, if present.
 */
function getEditedByReferenceFromQuestionnaireResponse(
  questionnaireResponse: QuestionnaireResponse,
): string | undefined {
  const editedByReference = getExtensionValues(questionnaireResponse, Extensions.EditedBy)?.[0] as
    | Reference
    | undefined;
  return editedByReference?.reference;
}

function resolvePractitionerReferenceFromQuestionnaireResponse(
  questionnaireResponse: QuestionnaireResponse,
): Reference<Practitioner> | undefined {
  const editedByReferenceValue =
    getEditedByReferenceFromQuestionnaireResponse(questionnaireResponse);
  if (isPractitionerReferenceString(editedByReferenceValue)) {
    return { reference: editedByReferenceValue };
  }

  const authorReference = questionnaireResponse.author?.reference;
  if (isPractitionerReferenceString(authorReference)) {
    return { reference: authorReference };
  }

  return undefined;
}

/**
 * Reads a Practitioner reference from in-memory amend Provenance snapshot observation targets.
 */
async function resolvePractitionerReferenceFromAmendProvenanceSnapshot(
  medplum: MedplumClient,
  amendProvenanceSnapshot: LaborAmendProvenanceSnapshot | undefined,
): Promise<Reference<Practitioner> | undefined> {
  for (const target of amendProvenanceSnapshot?.targets ?? []) {
    if (!target.reference?.startsWith("Observation/")) {
      continue;
    }

    try {
      const resource = await medplum.readReference(target);
      if (resource.resourceType !== "Observation") {
        continue;
      }

      const resolvedReference = resolvePractitionerReferenceFromObservation(resource);
      if (resolvedReference) {
        return resolvedReference;
      }
    } catch {
      continue;
    }
  }

  return undefined;
}

function isObservationLinkedToAmendVersion(
  observation: Observation,
  encounterQuestionnaireResponseVersionId: string,
): boolean {
  return (
    getObservationEncounterAmendParentVersionId(observation) ===
    encounterQuestionnaireResponseVersionId
  );
}

function sortQuestionnaireResponsesById(
  questionnaireResponses: QuestionnaireResponse[],
): QuestionnaireResponse[] {
  return [...questionnaireResponses].sort((left, right) =>
    (left.id ?? "").localeCompare(right.id ?? ""),
  );
}

function sortObservationsById(observations: Observation[]): Observation[] {
  return [...observations].sort((left, right) => (left.id ?? "").localeCompare(right.id ?? ""));
}

/**
 * Returns a single Practitioner reference when all resolved candidates agree.
 * Returns undefined when there are no candidates or when candidates disagree.
 */
function resolveUniquePractitionerReference(
  references: Array<Reference<Practitioner> | undefined>,
): Reference<Practitioner> | undefined {
  const uniqueReferenceStrings = new Set(
    references
      .map((reference) => reference?.reference)
      .filter((referenceString): referenceString is string => Boolean(referenceString)),
  );

  if (uniqueReferenceStrings.size !== 1) {
    return undefined;
  }

  return { reference: [...uniqueReferenceStrings][0]! };
}

function hasConflictingPractitionerReferences(
  references: Array<Reference<Practitioner> | undefined>,
): boolean {
  const uniqueReferenceStrings = new Set(
    references
      .map((reference) => reference?.reference)
      .filter((referenceString): referenceString is string => Boolean(referenceString)),
  );

  return uniqueReferenceStrings.size > 1;
}

/**
 * Resolves the Provenance performer from the signed-in profile or amend-specific flowsheet authorship.
 * Does not fall back to encounter participants to avoid misattributing amend authorship.
 */
export async function resolveLaborAmendProvenancePractitionerReference({
  medplum,
  clientId,
  encounterId,
  encounterQuestionnaireResponseVersionId,
  practitionerReference,
  stampedFlowsheetQuestionnaireResponses,
  amendProvenanceSnapshot,
}: ResolveLaborAmendProvenancePractitionerParams): Promise<Reference<Practitioner> | undefined> {
  if (isPractitionerReference(practitionerReference)) {
    return practitionerReference;
  }

  const snapshotResolvedReference = await resolvePractitionerReferenceFromAmendProvenanceSnapshot(
    medplum,
    amendProvenanceSnapshot,
  );
  if (snapshotResolvedReference) {
    return snapshotResolvedReference;
  }

  const sortedStampedFlowsheetQuestionnaireResponses = sortQuestionnaireResponsesById(
    stampedFlowsheetQuestionnaireResponses,
  );

  const flowsheetPractitionerReferences = sortedStampedFlowsheetQuestionnaireResponses
    .filter(
      (questionnaireResponse) =>
        getQuestionnaireResponseEncounterParentVersionId(questionnaireResponse) ===
        encounterQuestionnaireResponseVersionId,
    )
    .map((questionnaireResponse) =>
      resolvePractitionerReferenceFromQuestionnaireResponse(questionnaireResponse),
    );

  const resolvedFlowsheetReference = resolveUniquePractitionerReference(
    flowsheetPractitionerReferences,
  );
  if (resolvedFlowsheetReference) {
    return resolvedFlowsheetReference;
  }

  if (hasConflictingPractitionerReferences(flowsheetPractitionerReferences)) {
    return undefined;
  }

  const hasStampedFlowsheetQuestionnaireResponses =
    sortedStampedFlowsheetQuestionnaireResponses.some(
      (questionnaireResponse) =>
        getQuestionnaireResponseEncounterParentVersionId(questionnaireResponse) ===
        encounterQuestionnaireResponseVersionId,
    );

  if (!hasStampedFlowsheetQuestionnaireResponses) {
    return undefined;
  }

  const observationsResult = await fetchAllPaginatedResources<Observation>(medplum, "Observation", {
    encounter: joinReferenceString("Encounter", encounterId),
    subject: joinReferenceString("Patient", clientId),
    "status:not": "cancelled",
  });

  if (!observationsResult.success) {
    Sentry.captureException(observationsResult.error, {
      level: "warning",
      extra: { encounterId, clientId },
    });
    return undefined;
  }

  const observationPractitionerReferences = sortObservationsById(observationsResult.data)
    .filter((observation) =>
      isObservationLinkedToAmendVersion(observation, encounterQuestionnaireResponseVersionId),
    )
    .map((observation) => resolvePractitionerReferenceFromObservation(observation));

  return resolveUniquePractitionerReference(observationPractitionerReferences);
}
